import { useState } from "react";

import useAxiosInstance from "../../axios/useAxiosInstance";
import { objectToQueryString } from "../../utils/queryParamsHelpers";
import { API_STATUS, STATUS_CODES } from "../../consts/constants";
import { GENERIC_GET_API_FAILED_ERROR_MESSAGE } from "../../consts/constants";

type Methods = "head" | "options" | "put" | "post" | "patch" | "delete" | "get";

interface OtherOptionsInterface {
  isForNonLoggedInUser?: boolean;
  skipApiCallOnMount?: boolean;
  apiVersion?: string;
  apiModule?: string;
  apiBaseUrl?: string;
}

interface UseApiRequestParamsInterface {
  method: Methods;
  url: string;
  apiOptions?: Object;
  otherOptions?: OtherOptionsInterface;
}

interface MakeRequestParams {
  overrideUrl?: string;
  body?: object;
  queryParamsObject?: object;
  onSuccessCallback?: Function;
  onErrorCallback?: Function;
}

const useApiRequest = (params: UseApiRequestParamsInterface) => {
  const {
    method = "post",
    url,
    apiOptions = {},
    otherOptions = {},
  } = params || {};

  const [data, setData] = useState<any | null>(null);
  const [apiStatus, setApiStatus] = useState<string>(API_STATUS.IDLE);
  const [error, setError] = useState<Object | null>(null);

  const { isForNonLoggedInUser, apiVersion, apiModule, apiBaseUrl } = otherOptions;

  const { axiosInstance } = useAxiosInstance({
    omitToken: isForNonLoggedInUser,
    apiVersion,
    apiModule,
    apiBaseUrl,
  });

  const makeRequest = async ({
    overrideUrl, // Allows the user to override the default URL if needed
    body,
    queryParamsObject = {},
    onSuccessCallback,
    onErrorCallback,
  }: MakeRequestParams) => {
    const requestUrl = overrideUrl || url; // Use overrideUrl if provided, otherwise use the default url
    let modifiedURL = requestUrl;
    if (queryParamsObject && objectToQueryString(queryParamsObject)) {
      modifiedURL = `${requestUrl}?${objectToQueryString(queryParamsObject)}`;
    }
    try {
      setApiStatus(API_STATUS.LOADING);
      error && setError("");
      const response = await axiosInstance[method](
        modifiedURL,
        body,
        apiOptions
      );
      if (
        response.status === STATUS_CODES.SUCCESS_STATUS ||
        response.status === STATUS_CODES.RECORD_CREATED
      ) {
        setApiStatus(API_STATUS.SUCCESS);
        setData(response.data);
        onSuccessCallback && onSuccessCallback(response.data);
        return response.data;
      }
      setApiStatus(API_STATUS.ERROR);
      setError(GENERIC_GET_API_FAILED_ERROR_MESSAGE);
      onErrorCallback && onErrorCallback(GENERIC_GET_API_FAILED_ERROR_MESSAGE);
    } catch (err: any) {
      setApiStatus(API_STATUS.ERROR);
      if (!!err.response?.data?.message) {
        setError(err.response?.data?.message);
        if (
          err.response?.data?.data &&
          err.response?.data?.data?.errors &&
          Object.entries(err.response?.data?.data?.errors).length > 0
        ) {
          onErrorCallback && onErrorCallback(err.response?.data?.data);
        } else {
          onErrorCallback && onErrorCallback(err.response?.data?.message);
        }
        return;
      }
      setError(GENERIC_GET_API_FAILED_ERROR_MESSAGE);
      onErrorCallback &&
        onErrorCallback(
          err?.response?.data?.message || GENERIC_GET_API_FAILED_ERROR_MESSAGE
        );
    }
  };

  const isLoading = apiStatus === API_STATUS.LOADING;
  const isSuccess = apiStatus === API_STATUS.SUCCESS;
  const isError = apiStatus === API_STATUS.ERROR;

  return {
    apiStatus,
    data,
    error,
    makeRequest,
    isError,
    isLoading,
    isSuccess,
    setData,
    setError,
  };
};

interface SubmitRequestParams {
  url: string;
  apiOptions?: Object;
  otherOptions?: OtherOptionsInterface;
}

export const usePost = ({
  url,
  apiOptions,
  otherOptions,
}: SubmitRequestParams) =>
  useApiRequest({ method: "post", url, apiOptions, otherOptions });

export const usePut = ({
  url,
  apiOptions,
  otherOptions,
}: SubmitRequestParams) =>
  useApiRequest({ method: "put", url, apiOptions, otherOptions });

export const usePatch = ({
  url,
  apiOptions,
  otherOptions,
}: SubmitRequestParams) =>
  useApiRequest({ method: "patch", url, apiOptions, otherOptions });

export const useDelete = ({
  url,
  apiOptions,
  otherOptions,
}: SubmitRequestParams) =>
  useApiRequest({ method: "delete", url, apiOptions, otherOptions });
